import { celluleTexte, documentCsv, nomFichierCsv } from '@gestion/shared/csv'

/**
 * HISTORIQUE DES PORTES — les calculs purs (24/10/2026).
 *
 * Rien ici ne touche React, le routeur ni le document : la phrase de nuit, le
 * nom du fichier, le message d'export et le CSV se testent sans navigateur
 * (tests/access-historique-soiree.test.ts). Seul import : l'écrivain CSV
 * partagé avec le serveur (`@gestion/shared/csv`, sans dépendance).
 *
 * La nuit est fixée par le SERVEUR (history-period.ts) : la page n'envoie
 * qu'une clé (`?periode=soiree`), reçoit la nuit retenue et l'écrit en clair.
 * Les clés d'adresse gardent « soiree » ; les libellés disent « nuit »
 * (décision d'Hugo du 24/10/2026).
 */

/** Miroir d'`AccessHistoryNight` (packages/core/src/access/history-period.ts). */
export interface NuitHistorique {
  key: 'soiree' | 'soiree-precedente'
  /** AAAA-MM-JJ : la date de la nuit (celle du soir). */
  date: string
  /** « sam. 24 octobre ». */
  label: string
  /** Début inclus, ISO. */
  from: string
  /** Fin exclue, ISO. */
  to: string
  ongoing: boolean
}

/** Un événement tel que l'historique et son export le renvoient. */
export interface EvenementHistorique {
  at: string | null; actor: string | null; door: string | null; method: string | null
  result: string | null; classe: string; message: string | null
}

/** Les passages d'une porte sur la nuit en cours (lien de la fiche porte, menu d'une porte). */
export const cheminPassagesPorteSoiree = (id: string): string =>
  `/securite/controle-acces/historique?porte=${encodeURIComponent(id)}&periode=soiree`

/** « 08:00 », heure de Paris, quel que soit le fuseau du poste. */
const heureParis = (iso: string): string =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })

/**
 * La phrase écrite sous les filtres :
 *   « Nuit du sam. 24 octobre : passages depuis 08:00, heure de Paris. »
 *   « Nuit du ven. 23 octobre : de 08:00 à 08:00 le lendemain, heure de Paris. »
 */
export function phraseNuitHistorique(n: NuitHistorique): string {
  return n.ongoing
    ? `Nuit du ${n.label} : passages depuis ${heureParis(n.from)}, heure de Paris.`
    : `Nuit du ${n.label} : de ${heureParis(n.from)} à ${heureParis(n.to)} le lendemain, heure de Paris.`
}

const GLISSANTES = new Set(['24h', '7d', '30d'])
const DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Le nom du fichier. Une nuit se nomme par sa DATE, jamais « en cours » : le
 * fichier sera relu le lendemain.
 *   historique-acces-nuit-2026-10-24.csv · historique-acces-24h.csv · historique-acces.csv
 */
export function nomFichierHistorique(periode: string, nuit: NuitHistorique | null): string {
  if (nuit && DATE.test(nuit.date)) return nomFichierCsv('historique-acces', 'nuit', nuit.date)
  if (GLISSANTES.has(periode)) return nomFichierCsv('historique-acces', periode)
  return nomFichierCsv('historique-acces')
}

const nombre = (n: number): string => new Intl.NumberFormat('fr-FR').format(n)

/**
 * Le message de l'export :
 *   « 312 événements exportés. » · « 1 événement exporté. »
 *   « Export limité aux 5 000 événements les plus récents sur 7 412 : resserrez la période. »
 */
export function messageExport(n: number, total: number, tronque: boolean, max: number): string {
  if (tronque) return `Export limité aux ${nombre(max)} événements les plus récents sur ${nombre(total)} : resserrez la période.`
  return n > 1 ? `${nombre(n)} événements exportés.` : `${nombre(n)} événement exporté.`
}

/** Les colonnes, inchangées depuis l'export de la page. */
export const ENTETE_HISTORIQUE = ['date', 'acteur', 'porte', 'methode', 'resultat', 'classe', 'message'] as const

/** Le CSV : BOM, « ; », CRLF, guillemets doublés, têtes de formule neutralisées. */
export function csvHistorique(events: readonly EvenementHistorique[]): string {
  return documentCsv(ENTETE_HISTORIQUE, events.map((e) => [
    celluleTexte(e.at), celluleTexte(e.actor), celluleTexte(e.door), celluleTexte(e.method),
    celluleTexte(e.result), celluleTexte(e.classe), celluleTexte(e.message),
  ]))
}
